import { Link } from 'react-router-dom'
import { Shield, Car, Home, FileText, FolderOpen, ChevronRight, TrendingUp, Building2 } from 'lucide-react'
import type { HubItem } from '@/types'
import { CATEGORIES } from '@/lib/categories'
import { cn, formatDate, formatCurrency, expiryStatus } from '@/lib/utils'
import ExpiryBadge from './ExpiryBadge'

const ICONS = { Shield, Car, Home, FileText, FolderOpen, TrendingUp, Building2 }

interface Props {
  item: HubItem
  compact?: boolean
}

export default function ItemCard({ item, compact = false }: Props) {
  const catConfig = CATEGORIES[item.category]
  const subConfig = catConfig?.subcategories[item.subcategory]
  const Icon = ICONS[catConfig?.icon as keyof typeof ICONS] ?? FolderOpen
  const status = expiryStatus(item.expiry_date)

  const borderAccent: Record<typeof status, string> = {
    expired:  'border-l-red-500',
    critical: 'border-l-red-400',
    warning:  'border-l-amber-400',
    upcoming: 'border-l-blue-400',
    ok:       'border-l-transparent',
    none:     'border-l-transparent',
  }

  return (
    <Link
      to={`/items/${item.id}`}
      className={cn(
        'block bg-white rounded-xl border border-slate-200 border-l-4 shadow-sm hover:shadow-md active:scale-[0.99] transition-all',
        borderAccent[status],
        compact ? 'p-3' : 'p-4'
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn('rounded-lg p-2 flex-shrink-0', catConfig?.bgColor)}>
          <Icon size={compact ? 16 : 18} className={catConfig?.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className={cn('font-medium text-slate-900 truncate', compact ? 'text-sm' : 'text-base')}>
                {item.title}
              </p>
              {!compact && (
                <p className="text-sm text-slate-500 truncate">
                  {subConfig?.label ?? item.subcategory}
                  {item.provider ? ` · ${item.provider}` : ''}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <ExpiryBadge expiryDate={item.expiry_date} />
              <ChevronRight size={16} className="text-slate-300" />
            </div>
          </div>
          {!compact && (item.expiry_date || item.amount) && (
            <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
              {item.expiry_date && (
                <span>Expires {formatDate(item.expiry_date)}</span>
              )}
              {item.amount && (
                <span>{formatCurrency(item.amount)}{item.amount_frequency ? `/${item.amount_frequency}` : ''}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  )
}
